import { startOfDay, startOfWeek, startOfMonth, isSunday, format, isAfter, isSameDay, subMonths } from 'date-fns';
import { Timestamp } from 'firebase/firestore'; // Needed for updates
import { DAILY_MANDATORY, WEEKLY_MANDATORY, MONTHLY_MANDATORY } from './choreConstants';

// Helper: Get start of period
export const getPeriodStart = (frequency, today = new Date()) => {
    if (frequency === 'weekly') return startOfWeek(today, { weekStartsOn: 0 });
    if (frequency === 'monthly') return subMonths(today, 1);
    return startOfDay(today);
};

const DATE_FORMAT = 'yyyy-MM-dd';

/**
 * Pure function to calculate Kanban board state and qualification status
 * @param {string} userId - ID of the user (e.g., 'ryan')
 * @param {Array} dbChores - List of all chores from Firestore 'chores' collection
 * @param {Object} fixedStatus - The 'fixed_chore_status' document for this user
 * @param {Object} [optimisticParams] - Optional optimistic updates
 * @param {Object} optimisticParams.status - { [choreId]: status }
 * @param {Object} optimisticParams.order - { todo: [id...], doing: [id...], done: [id...] }
 * @returns {Object} { kanbanBoard, qualificationStatus, quotaStats }
 */
export const calculateChoreState = (userId, dbChores, fixedStatus = {}, optimisticParams = {}) => {
    if (!userId) {
        return {
            kanbanBoard: { todo: [], doing: [], done: [] },
            qualificationStatus: { qualified: false },
            quotaStats: { daily: { current: 0, target: 4 }, weekly: { current: 0, target: 2 }, monthly: { current: 0, target: 1 } }
        };
    }

    const { status: optimisticStatus = {}, order: optimisticOrder = {} } = optimisticParams;

    const today = new Date();
    const todayStr = format(today, DATE_FORMAT);
    const isSun = isSunday(today);
    
    // Buckets before sorting
    const rawColumns = { todo: [], doing: [], done: [] };

    // Helper to add task to correct bucket
    const addToBucket = (task, status) => {
        // Check optimistic status override
        const finalStatus = optimisticStatus[task.id] || status;
        
        if (rawColumns[finalStatus]) {
            rawColumns[finalStatus].push(task);
        } else {
            // Fallback if status is weird, though shouldn't happen
            rawColumns.todo.push(task);
        }
    };

    // --- A. Fixed Daily Chores ---
    const dailyStatus = fixedStatus[todayStr] || {};
    
    // Check local defined constant or import if needed, but assuming defined in scope or imported
    // Imports show ./choreConstants usage in original file but lines 2 was imports.
    // I need to make sure I include imports correctly.
    // Original imports: 
    // import { DAILY_MANDATORY, WEEKLY_MANDATORY, MONTHLY_MANDATORY } from './choreConstants';
    
    const DAILY_MANDATORY_LIST = ['Make Bed', 'Brush Teeth', 'Clean Room']; // Placeholder if constants not available? 
    // The original file imported them. I MUST include that import.
    // Checking file content from step 14:
    // import { DAILY_MANDATORY, WEEKLY_MANDATORY, MONTHLY_MANDATORY } from './choreConstants';
    
    // I will use strict content from step 14 but keep imports.
    // Wait, I need to copy the imports exactly.

    DAILY_MANDATORY.forEach(name => {
        const status = dailyStatus[name] || 'todo';
        const last = dailyStatus[name + '_last'];
        
        addToBucket({
            id: `fixed_daily_${name}`,
            title: name,
            type: 'fixed_daily',
            originalStatus: status,
            last
        }, status);
    });

    // --- B. Weekly/Monthly Fixed Chores (Not on Sunday) ---
    if (!isSun) {
        // Weekly
        const weekKey = `week_${format(startOfWeek(today), DATE_FORMAT)}`;
        const weeklyStatusMap = fixedStatus[weekKey] || {};
        
        WEEKLY_MANDATORY.forEach(name => {
            const status = weeklyStatusMap[name] || 'todo';
            const last = weeklyStatusMap[name + '_last'];
            const first = weeklyStatusMap[name + '_first'];
            
            // If we have a 'first' record for this week, it was done.
            // If status is NOT done, then it's "Done but moved back".
            const isPeriodCompleted = !!first && status !== 'done';

            addToBucket({
                id: `fixed_weekly_${name}`,
                title: name,
                type: 'fixed_weekly',
                originalStatus: status,
                last, // Add timestamp
                isPeriodCompleted
            }, status);
        });

        // Monthly
        const monthKey = `month_${format(startOfMonth(today), DATE_FORMAT)}`;
        const monthlyStatusMap = fixedStatus[monthKey] || {};

        MONTHLY_MANDATORY.forEach(name => {
            const status = monthlyStatusMap[name] || 'todo';
            const last = monthlyStatusMap[name + '_last'];
            const first = monthlyStatusMap[name + '_first'];
            
            const isPeriodCompleted = !!first && status !== 'done';

            addToBucket({
                id: `fixed_monthly_${name}`,
                title: name,
                type: 'fixed_monthly',
                originalStatus: status,
                last, // Add timestamp
                isPeriodCompleted
            }, status);
        });
    }

    // --- C. DB Chores (Grab Bag + Others) ---
    const userName = userId.charAt(0).toUpperCase() + userId.slice(1);
    
    const myChores = dbChores.filter(c => {
        if (c.assignee === userName) return true;
        if (c.assignee === 'All') return true;
        if (c.assignee === 'All Kids' && ['Ryan', 'Leah', 'Samantha', 'Connor', 'Marie'].includes(userName)) return true;
        return false;
    });

    myChores.forEach(chore => {
        let status = 'todo';
        
        // Check Done
        if (chore.last) {
            const lastDate = chore.last.toDate ? chore.last.toDate() : new Date(chore.last);
            let periodStart;
            if (chore.frequency === 'weekly') periodStart = startOfWeek(today, { weekStartsOn: 0 });
            // ROLLING MONTH LOGIC: subMonths(today, 1)
            else if (chore.frequency === 'monthly') periodStart = subMonths(today, 1);
            else periodStart = startOfDay(today); // Daily

            if (isAfter(lastDate, periodStart)) {
                status = 'done';
                
                // Filter: If done by someone else, hide it entirely
                if (chore.lastBy && chore.lastBy !== userName) {
                    return;
                }
            }
        }
        
        // Check Doing / Active Claim
        if (chore.claimedBy && chore.claimedAt) {
             const claimedDate = chore.claimedAt.toDate ? chore.claimedAt.toDate() : new Date(chore.claimedAt);
             const isClaimActive = isSameDay(today, claimedDate);

             if (isClaimActive) {
                 if (chore.claimedBy === userName) {
                     if (status !== 'done') status = 'doing'; // Own claim
                 } else {
                     // CLAIMED BY SOMEONE ELSE TODAY -> hide it (Exclusive)
                     if (status !== 'done') return; 
                 }
             }
        }
        
        // Determine if this is an "unverified" shared chore (Done but no lastBy)
        const isShared = chore.assignee === 'All' || chore.assignee === 'All Kids';
        const isUnverified = status === 'done' && isShared && !chore.lastBy;
        
        // Determine isPeriodCompleted for DB Chores
        let isPeriodCompleted = false;
        if (chore.periodFirst && status !== 'done') {
             const firstDate = chore.periodFirst.toDate ? chore.periodFirst.toDate() : new Date(chore.periodFirst);
             let periodStart;
             if (chore.frequency === 'weekly') periodStart = startOfWeek(today, { weekStartsOn: 0 });
             else if (chore.frequency === 'monthly') periodStart = subMonths(today, 1);
             else periodStart = startOfDay(today); 
             
             if (isAfter(firstDate, periodStart)) {
                 isPeriodCompleted = true;
             }
        }

        addToBucket({
            id: chore.id,
            title: chore.name || chore.description,
            type: 'db',
            frequency: chore.frequency,
            points: chore.points,
            originalChore: chore,
            isUnverified, // Add flag
            isPeriodCompleted
        }, status);
    });

    // --- D. Sorting ---
    const sortedColumns = { todo: [], doing: [], done: [] };
    
    ['todo', 'doing', 'done'].forEach(colId => {
        const bucket = rawColumns[colId];
        const orderList = optimisticOrder[colId] || fixedStatus?.order?.[colId] || [];
        
        // Create a map for quick lookup of order priority
        const orderMap = new Map();
        orderList.forEach((id, index) => orderMap.set(id, index));
        
        sortedColumns[colId] = bucket.sort((a, b) => {
            const idxA = orderMap.has(a.id) ? orderMap.get(a.id) : 999999;
            const idxB = orderMap.has(b.id) ? orderMap.get(b.id) : 999999;
            
            if (idxA !== idxB) return idxA - idxB;
            
            // Secondary sort: Alphabetical or creation?
            return a.title.localeCompare(b.title);
        });
    });

    const kanbanBoard = sortedColumns;

    // --- E. Qualification Calculation ---
    const { done } = kanbanBoard;
    let qualificationStatus = { qualified: true };

    // Check Daily Mandatory
    const mandatoryDone = DAILY_MANDATORY.every(m => 
        done.some(t => t.type === 'fixed_daily' && t.title === m)
    );

    if (!mandatoryDone) {
        qualificationStatus = { qualified: false, missing: 'Daily Mandatory' };
    } else if (!isSunday(new Date())) {
        // Check Daily Grab Bag (Quota: 4)
        const grabBagDoneCount = done.filter(t => t.type === 'db' && t.frequency === 'daily' && !t.isUnverified).length;
        if (grabBagDoneCount < 4) {
            qualificationStatus = { qualified: false, missing: `Grab Bag (${grabBagDoneCount}/4)` };
        } else {
            // Check Weekly Grab Bag + Fixed Weekly
            const weeklyDoneTotal = done.filter(t => 
                (t.type === 'db' && t.frequency === 'weekly' && !t.isUnverified) ||
                (t.type === 'fixed_weekly')
            ).length;
            
            // How many done TODAY?
            const weeklyDoneToday = done.filter(t => {
                if (t.isUnverified) return false;
                
                // Allow DB Weekly OR Fixed Weekly
                if ( !((t.type === 'db' && t.frequency === 'weekly') || t.type === 'fixed_weekly') ) return false;

                // Check timestamp
                const lastVal = (t.type === 'fixed_weekly') ? t.last : t.originalChore.last;
                if (!lastVal) return false; 

                const lastDate = lastVal.toDate ? lastVal.toDate() : new Date(lastVal);
                return isSameDay(lastDate, today);
            }).length;

            if (weeklyDoneTotal < 7) {
                if (weeklyDoneToday < 2) {
                    qualificationStatus = { qualified: false, missing: `Weekly Chores (${weeklyDoneToday}/2)` };
                }
            }

            if (qualificationStatus.qualified) {
                // Check Monthly
                const monthlyDoneTotal = done.filter(t => 
                    (t.type === 'db' && t.frequency === 'monthly' && !t.isUnverified) ||
                    (t.type === 'fixed_monthly')
                ).length;

                if (monthlyDoneTotal < 7) {
                    const monthlyDoneToday = done.filter(t => {
                        if (t.isUnverified) return false;

                        // Allow DB Monthly OR Fixed Monthly
                        if ( !((t.type === 'db' && t.frequency === 'monthly') || t.type === 'fixed_monthly') ) return false;

                        // Check timestamp
                        const lastVal = (t.type === 'fixed_monthly') ? t.last : t.originalChore.last;
                        if (!lastVal) return false;

                        const lastDate = lastVal.toDate ? lastVal.toDate() : new Date(lastVal);
                        return isSameDay(lastDate, today);
                    }).length;
                    
                    if (monthlyDoneToday < 1) {
                        qualificationStatus = { qualified: false, missing: `Monthly Chore (${monthlyDoneToday}/1)` };
                    }
                }
            }
        }
    }

    // --- F. Detailed Quota Stats ---
    const dailyCurrent = done.filter(t => t.type === 'db' && t.frequency === 'daily' && !t.isUnverified).length;

    const weeklyDoneTotal = done.filter(t => 
        (t.type === 'db' && t.frequency === 'weekly' && !t.isUnverified) ||
        (t.type === 'fixed_weekly')
    ).length;

    const weeklyDoneToday = done.filter(t => {
        if (t.isUnverified) return false;
        
        // Allow DB Weekly OR Fixed Weekly
        if ( !((t.type === 'db' && t.frequency === 'weekly') || t.type === 'fixed_weekly') ) return false;

        const lastVal = (t.type === 'fixed_weekly') ? t.last : t.originalChore.last;
        if (!lastVal) return false;

        const lastDate = lastVal.toDate ? lastVal.toDate() : new Date(lastVal);
        return isSameDay(lastDate, today);
    }).length;
    
    const monthlyDoneTotal = done.filter(t => 
        (t.type === 'db' && t.frequency === 'monthly' && !t.isUnverified) ||
        (t.type === 'fixed_monthly')
    ).length;

    const monthlyDoneToday = done.filter(t => {
        if (t.isUnverified) return false;
        
        // Allow DB Monthly OR Fixed Monthly
        if ( !((t.type === 'db' && t.frequency === 'monthly') || t.type === 'fixed_monthly') ) return false;

        const lastVal = (t.type === 'fixed_monthly') ? t.last : t.originalChore.last;
        if (!lastVal) return false;
        
        const lastDate = lastVal.toDate ? lastVal.toDate() : new Date(lastVal);
        return isSameDay(lastDate, today);
    }).length;

    const mandatoryDoneCount = DAILY_MANDATORY.reduce((acc, m) => {
        const isDone = done.some(t => t.type === 'fixed_daily' && t.title === m);
        return acc + (isDone ? 1 : 0);
    }, 0);

    const quotaStats = {
        daily: { current: dailyCurrent, target: isSun ? 0 : 4 },
        weekly: { current: weeklyDoneToday, target: isSun ? 0 : 2, totalWeek: weeklyDoneTotal },
        monthly: { current: monthlyDoneToday, target: isSun ? 0 : 1, totalMonth: monthlyDoneTotal },
        mandatory: { current: mandatoryDoneCount, target: DAILY_MANDATORY.length }
    };

    return { kanbanBoard, qualificationStatus, quotaStats };
};

/**
 * Calculates updates for Firestore based on chore movement
 * @param {Object} moveParams 
 * @param {string} moveParams.taskId
 * @param {string} moveParams.destColId
 * @param {string} moveParams.userId
 * @param {Object} moveParams.task - The task object from local state (for context)
 * @param {Object} [moveParams.dbData] - Fresh data from DB (for transaction)
 * @returns {Object} { ref: DocRef, updates: Object, restoredDate: Date|null }
 */
export const calculateChoreUpdate = ({ taskId, destColId, userId, task, dbData }) => {
    const today = new Date();
    const todayStr = format(today, DATE_FORMAT);
    const result = { updates: {}, restoredDate: null };

    if (task.type.startsWith('fixed')) {
       // Fixed Chore Logic
       // dbData is the 'fixed_chore_status' user doc
       let keyGroup;
       if (task.type === 'fixed_daily') keyGroup = todayStr;
       else if (task.type === 'fixed_weekly') keyGroup = `week_${format(startOfWeek(today), DATE_FORMAT)}`;
       else if (task.type === 'fixed_monthly') keyGroup = `month_${format(startOfMonth(today), DATE_FORMAT)}`;

       const fieldPath = `${keyGroup}.${task.title}`;
       const lastPath = fieldPath + '_last';
       const firstPath = fieldPath + '_first';
       
       result.updates[fieldPath] = destColId;

       if (destColId === 'done') {
           // Check existing first completion
           const existingFirst = dbData?.[keyGroup]?.[task.title + '_first'];
           
           if (existingFirst) {
               // RESTORE
               result.updates[lastPath] = existingFirst;
               result.restoredDate = existingFirst;
           } else {
               // NEW
               const nowTs = Timestamp.fromDate(new Date());
               result.updates[lastPath] = nowTs;
               if (task.type !== 'fixed_daily') {
                    result.updates[firstPath] = nowTs;
               }
           }
       } else {
           // Undo
           result.updates[lastPath] = null;
           // Keep firstPath (History)
       }
       
    } else if (task.type === 'db') {
       // DB Chore Logic
       // dbData is the specific chore doc
       if (destColId === 'done') {
           let restore = false;
           let storedFirst = dbData?.periodFirst;

           if (task.frequency === 'weekly' || task.frequency === 'monthly') {
               if (storedFirst) {
                   const firstDate = storedFirst.toDate ? storedFirst.toDate() : new Date(storedFirst);
                   const cutoff = getPeriodStart(task.frequency, today);
                   
                   if (firstDate >= cutoff) {
                       restore = true;
                       result.restoredDate = storedFirst;
                   }
               }
           }

           if (restore) {
               result.updates.last = result.restoredDate;
               result.updates.lastBy = userId.charAt(0).toUpperCase() + userId.slice(1);
               result.updates.claimedAt = null;
               result.updates.claimedBy = null;
           } else {
               const nowTs = Timestamp.fromDate(new Date());
               result.updates.last = nowTs;
               result.updates.periodFirst = nowTs;
               result.updates.lastBy = userId.charAt(0).toUpperCase() + userId.slice(1);
               result.updates.claimedAt = null;
               result.updates.claimedBy = null;
           }
       } else if (destColId === 'doing') {
           result.updates.claimedAt = Timestamp.fromDate(new Date());
           result.updates.claimedBy = userId.charAt(0).toUpperCase() + userId.slice(1);
           result.updates.last = null;
           
           // SELF HEAL: If moving legacy chore (no periodFirst but has last), save history
           const currentFirst = dbData?.periodFirst;
           const currentLast = dbData?.last; 
           
            // If we don't have a 'first' record, but we DO have a 'last' record (legacy completion),
            // save that 'last' as the 'first' so we can restore to it later if needed.
           if (!currentFirst && currentLast) {
                result.updates.periodFirst = currentLast;
           }
       } else {
           result.updates.claimedAt = null;
           result.updates.claimedBy = null;
           result.updates.last = null;
       }
    }

    return result;
};
